import { CHANNELS } from "../../shared/channels";
import { TASK_STATUSES, taskKey } from "../../shared/task_status";
import { TaskEntry, TaskSnapshot, TaskStatus } from "../../shared/types";
import { call } from "../bridge";
import { flashToast } from "../dom";
import { iconSvg } from "../icons";
import { t } from "../i18n";
import { buzz } from "../tweens";
import { calmMotion, openColumnPicker } from "../kanban";

// what you can do to a task from a row or a board card: pick its status, or trash it.
// statuses are chix only. nothing here writes to plane or the bot except trashing a chix task, which deletes it there

export const STATUS_LABEL: Record<TaskStatus, string> = { todo: "to do", doing: "doing", blocked: "blocked", done: "done" };

export { calmMotion };

function cleanError(error: unknown): string {
  return String(error instanceof Error ? error.message : error).replace(/^.*Error: /, "");
}

// the view that owns the snapshot hears about changes here, so it can animate them
let onChange: ((snapshot: TaskSnapshot) => void) | null = null;
export function onTaskAction(listener: (snapshot: TaskSnapshot) => void): void {
  onChange = listener;
}

export async function setStatus(task: TaskEntry, status: TaskStatus): Promise<boolean> {
  try {
    const fresh = await call<TaskSnapshot>(CHANNELS.tasksStatus, taskKey(task), status);
    if (fresh?.tasks) onChange?.(fresh);
    return true;
  } catch (error) {
    flashToast(cleanError(error));
    return false;
  }
}

// the same picker the boards use, one 44pt option per status
export function openStatusPicker(task: Pick<TaskEntry, "title">, current: TaskStatus, pick: (status: TaskStatus) => void): void {
  openColumnPicker(task.title, TASK_STATUSES.map((id) => ({ id, label: t(STATUS_LABEL[id]) })), current, (id) => pick(id as TaskStatus));
}

// the status chip, also the way to change it
export function buildStatusChip(task: Pick<TaskEntry, "title">, status: TaskStatus, pick: (status: TaskStatus) => void): HTMLButtonElement {
  const chip = document.createElement("button");
  chip.type = "button";
  chip.className = `task-status status-${status}`;
  chip.setAttribute("aria-label", t("status: {status}", { status: t(STATUS_LABEL[status]) }));
  const dot = document.createElement("i");
  dot.className = "task-dot";
  const label = document.createElement("span");
  label.textContent = t(STATUS_LABEL[status]);
  chip.append(dot, label, document.createTextNode(" ▾"));
  chip.addEventListener("click", (event) => {
    event.stopPropagation();
    openStatusPicker(task, status, pick);
  });
  return chip;
}

// trashing: a chix task is deleted from the bot, a plane task is only hidden here. two taps for the bot one
const ARM_MS = 3500;

export async function trashTask(task: TaskEntry, card: HTMLElement): Promise<void> {
  const crunch = !calmMotion() && card.isConnected;
  if (crunch) {
    card.classList.add("task-crunch");
    await new Promise((resolve) => window.setTimeout(resolve, 240));
  }
  try {
    if (task.source === "chix") await call(CHANNELS.chixComplete, task.id);
    else {
      const fresh = await call<TaskSnapshot>(CHANNELS.tasksHide, taskKey(task), true);
      if (fresh?.tasks) onChange?.(fresh);
      else flashToast(t("hidden here, still open in plane"));
    }
    buzz(12);
  } catch (error) {
    card.classList.remove("task-crunch");
    flashToast(cleanError(error));
  }
}

export function buildTrashButton(task: TaskEntry, card: () => HTMLElement): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "pill icon-only task-trash";
  button.innerHTML = `${iconSvg("trash", 20)}<span class="task-trash-sure"></span>`;
  button.querySelector(".task-trash-sure")!.textContent = t("sure?");
  button.setAttribute("aria-label", task.source === "chix" ? t("delete task") : t("hide task"));
  let timer = 0;
  const disarm = () => {
    window.clearTimeout(timer);
    button.classList.remove("armed");
  };
  const arm = () => {
    button.classList.add("armed");
    window.clearTimeout(timer);
    timer = window.setTimeout(disarm, ARM_MS);
  };
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    if (task.source === "chix" && !button.classList.contains("armed")) {
      arm();
      return;
    }
    disarm();
    void trashTask(task, card());
  });
  // dropping a chix card on the trash asks for the same second tap
  button.addEventListener("task-arm", arm);
  return button;
}
